import { useLoad } from "@redotech/react-util/load";
import { SignUpFormGoal } from "@redotech/redo-model/signup-form";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { RedoShopifyRpcClient } from "@redotech/shopify-sdk/rpc/client";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/util/shopify-id";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RedoShopifyRpcClientContext } from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { getVariantIdFromUrl } from "../utils";
import { BackInStockFormModal } from "./back-in-stock-form-modal";

/**
 * Hook to detect URL changes for variant selection
 * @param callback Function to call when URL changes
 */
export function useUrlChangeDetection(callback: () => void) {
  useEffect(() => {
    callback();

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      callback();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      callback();
    };

    // Also listen for popstate events (browser back/forward)
    addEventListener("popstate", callback);

    // Cleanup
    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      removeEventListener("popstate", callback);
    };
  }, [callback]);
}

/**
 * Hook to check if the current product variant is out of stock
 * @param rpcClient The RedoShopifyRpcClient instance
 * @returns Object containing stock status and loading state
 */
export function useVariantStockStatus(
  rpcClient: RedoShopifyRpcClient | undefined,
): {
  isOutOfStock: boolean;
  isCheckingStock: boolean;
  productId: string | null;
  variantId: string | null;
} {
  const [isOutOfStock, setIsOutOfStock] = useState<boolean>(false);
  const [isCheckingStock, setIsCheckingStock] = useState<boolean>(true);
  const [productId, setProductId] = useState<string | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);

  const checkCurrentVariantStock = useCallback(async () => {
    if (!rpcClient) {
      setIsCheckingStock(false);
      return;
    }

    try {
      setIsCheckingStock(true);

      const variantId = getVariantIdFromUrl(location.href);
      const productId: string | null = (
        globalThis as any
      ).ShopifyAnalytics?.meta?.product?.id.toString();

      if (!productId) {
        setIsOutOfStock(false);
        setIsCheckingStock(false);
        return;
      }

      const product = await rpcClient.getProductById({
        productId: String(productId),
      });

      if (!product || !product.variants || product.variants.length === 0) {
        setIsOutOfStock(false);
        setIsCheckingStock(false);
        return;
      }

      if (variantId) {
        const variant = product.variants.find(
          (v: (typeof product.variants)[number]) =>
            String(v.id) ===
            convertShopifyIdToGid(variantId, ShopifyIdType.ProductVariant),
        );
        if (variant) {
          setIsOutOfStock(!variant.available);
          setIsCheckingStock(false);
          setProductId(productId);
          setVariantId(variantId);
          return;
        }
      }

      const firstVariant = product.variants[0];
      if (firstVariant) {
        setIsOutOfStock(!firstVariant.available);
        setIsCheckingStock(false);
        setProductId(productId);
        setVariantId(firstVariant.id);
      } else {
        setIsOutOfStock(false);
      }

      setIsCheckingStock(false);
    } catch (error) {
      console.error("Error checking variant stock status:", error);
      setIsOutOfStock(false);
      setIsCheckingStock(false);
    }
  }, [rpcClient]);

  useUrlChangeDetection(checkCurrentVariantStock);

  return { isOutOfStock, isCheckingStock, productId, variantId };
}

/**
 * Main component for the Back in Stock PDP button
 */
export function BackInStockPdpButtonEntryPoint({
  autocheck,
}: {
  autocheck: WidgetAutocheck;
}) {
  const rpcClient = useContext(RedoShopifyRpcClientContext);
  const { isOutOfStock, isCheckingStock, productId, variantId } =
    useVariantStockStatus(rpcClient);

  const shopifyCustomerId = useMemo(
    () =>
      (
        globalThis as any
      ).ShopifyAnalytics?.meta?.page?.customerId?.toString() ?? null,
    [],
  );

  // Load back in stock form
  const backInStockFormLoad = useLoad(async () => {
    if (!rpcClient) {
      return [];
    }

    const forms = await rpcClient.getFormsByGoal({
      goal: SignUpFormGoal.BACK_IN_STOCK,
    });
    return forms.filter((f: (typeof forms)[number]) => f.publishedAt);
  }, [rpcClient]);

  const [backInStockForm, setBackInStockForm] = useState<
    { id: string; publishedAt: Date | null; form: SignupForm } | undefined
  >(undefined);

  useEffect(() => {
    if (backInStockFormLoad.value) {
      setBackInStockForm(backInStockFormLoad.value[0]);
    }
  }, [backInStockFormLoad.value]);

  if (isCheckingStock || !backInStockForm || !isOutOfStock) {
    return null;
  }

  log("Back in stock form", backInStockForm);

  return (
    <BackInStockFormModal
      brandKit={autocheck.brandKit}
      client={rpcClient}
      form={backInStockForm.form}
      formId={backInStockForm.id}
      productId={productId}
      shopifyCustomerId={shopifyCustomerId}
      variantId={variantId}
    />
  );
}
